import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../data/translations';
import { ArrowRight, ArrowLeft } from 'lucide-react';

const BASE = "https://www.cobras.be/sites/default/files/styles/";

const teamMembers = [
  { name: "Jelle Vandecasteele", role: { nl:"Founder & interieurarchitect", en:"Founder & Interior Architect", fr:"Fondateur & architecte d'intérieur", de:"Gründer & Innenarchitekt", es:"Fundador y arquitecto de interiores" }, image: BASE+"text_media_small/public/uploads/images/cobras_website_02_0.jpg", desc: { nl:"Analyse & ambitie document", en:"Analysis & ambition document", fr:"Analyse & document d'ambition", de:"Analyse & Ambitionsdokument", es:"Análisis y documento de ambición" }, featured: true },
  { name: "Willem Devos", role: { nl:"Founder & architect", en:"Founder & Architect", fr:"Fondateur & architecte", de:"Gründer & Architekt", es:"Fundador y arquitecto" }, image: BASE+"text_media_small/public/uploads/images/cobras_website_06_0.jpg", desc: { nl:"Analyse & detail design", en:"Analysis & detail design", fr:"Analyse & design détaillé", de:"Analyse & Detaildesign", es:"Análisis y diseño detallado" }, featured: true },
  { name: "Sarah Bollaert", role: { nl:"Interieurarchitecte", en:"Interior Architect", fr:"Architecte d'intérieur", de:"Innenarchitektin", es:"Arquitecta de interiores" }, image: BASE+"card_medium/public/uploads/images/cobras_website_08.jpg" },
  { name: "Valentina Cambien", role: { nl:"Interieurarchitecte", en:"Interior Architect", fr:"Architecte d'intérieur", de:"Innenarchitektin", es:"Arquitecta de interiores" }, image: BASE+"card_medium/public/uploads/images/cobras_website_03.jpg" },
  { name: "Justine Naessens", role: { nl:"Interieurarchitecte", en:"Interior Architect", fr:"Architecte d'intérieur", de:"Innenarchitektin", es:"Arquitecta de interiores" }, image: BASE+"card_medium/public/uploads/images/cobras_website_04.jpg" },
  { name: "Tom Vandendriessche", role: { nl:"Assistent architect", en:"Assistant Architect", fr:"Architecte assistant", de:"Assistent Architekt", es:"Arquitecto asistente" }, image: BASE+"card_medium/public/uploads/images/tom-hr.jpg" },
  { name: "Gilles Vanhecke", role: { nl:"Projectleider", en:"Project Manager", fr:"Chef de projet", de:"Projektleiter", es:"Director de proyecto" }, image: BASE+"card_medium/public/uploads/images/cobras_website_01.jpg" },
  { name: "Charlotte Deschrijver", role: { nl:"Administratie & boekhouding", en:"Administration & Accounting", fr:"Administration & comptabilité", de:"Verwaltung & Buchhaltung", es:"Administración y contabilidad" }, image: BASE+"card_medium/public/uploads/images/charlotte.jpg" },
];

const TeamPage = () => {
  const { lang } = useLanguage();
  const { t, get } = useTranslation(lang);
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { window.scrollTo(0,0); setTimeout(()=>setLoaded(true),100); }, []);
  const p = t.teamPage;

  return (
    <div className="min-h-screen" style={{background:'#0a0a0a'}}>
      <div className="fixed inset-0 opacity-30 pointer-events-none z-0" style={{backgroundImage:`url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`,backgroundSize:'256px'}} />
      <div className="relative z-10">
        {/* Hero */}
        <div className="pt-28 md:pt-36 pb-12 px-8 md:px-16 lg:px-24">
          <button onClick={()=>navigate('/')} className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-8 transition-colors"><ArrowLeft size={16}/> {get(t.common.home)}</button>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white italic leading-tight max-w-4xl" style={{opacity:loaded?1:0,transform:loaded?'translateY(0)':'translateY(30px)',transition:'all 0.8s cubic-bezier(0.16,1,0.3,1)'}}>{get(p.title)}</h1>
          <p className="text-gray-400 text-lg md:text-xl italic mt-6 max-w-3xl">{p.quote}</p>
        </div>

        {/* Team photo + Custom teams */}
        <div className="px-8 md:px-16 lg:px-24 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <svg viewBox="0 0 300 60" className="w-full max-w-[280px] opacity-30 mb-6" fill="none" stroke="white" strokeWidth="1">
                <path d="M0,30 L30,0 L60,30 L30,60 Z" /><path d="M40,30 L70,0 L100,30 L70,60 Z" /><path d="M80,30 L110,0 L140,30 L110,60 Z" /><path d="M120,30 L150,0 L180,30 L150,60 Z" /><path d="M160,30 L190,0 L220,30 L190,60 Z" />
              </svg>
              <img src={BASE+"text_media_small/public/uploads/images/cobras_website_11_vierkant_0.jpg"} alt="Cobras Team" className="w-full rounded-sm" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-6">{get(p.customTeams)}</h2>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">{get(p.customTeamsDesc)}</p>
              <ul className="space-y-2">
                {get(p.roles).map((r,i)=>(
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor:'#A855F7'}}/>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Expert section */}
        <div className="px-8 md:px-16 lg:px-24 mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-12">{get(p.expertTitle)}</h2>
          {/* Featured founders */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            {teamMembers.filter(m=>m.featured).map((m,i)=>(
              <div key={i} className="flex flex-col">
                <p className="text-sm uppercase tracking-[0.15em] mb-2" style={{color:'#A855F7'}}>{get(m.desc)}</p>
                <div className="overflow-hidden rounded-sm mb-4">
                  <img src={m.image} alt={m.name} className="w-full object-cover" style={{maxHeight:'500px'}}/>
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white">{m.name}</h3>
                <p className="text-gray-400 text-base">{get(m.role)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* COBRAS team grid */}
        <div className="px-8 md:px-16 lg:px-24 mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-8">{get(p.cobrasTeam)}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {teamMembers.filter(m=>!m.featured).map((m,i)=>(
              <div key={i} className="group">
                <div className="overflow-hidden rounded-sm mb-3">
                  <img src={m.image} alt={m.name} className="w-full aspect-[3/4] object-cover transition-transform duration-500 group-hover:scale-[1.04]"/>
                </div>
                <h4 className="text-lg font-bold text-white">{m.name}</h4>
                <p className="text-sm text-gray-400">{get(m.role)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Hiring */}
        <div className="px-8 md:px-16 lg:px-24 py-12">
          <div className="p-8 md:p-12 rounded-sm" style={{backgroundColor:'#111'}}>
            <p className="text-xl md:text-2xl font-bold text-white mb-4">{get(p.hiring)}</p>
            <p className="text-gray-400 mb-6">info@cobras.be</p>
          </div>
        </div>

        {/* Jordan Quote */}
        <div className="px-8 md:px-16 lg:px-24 py-12 text-center">
          <blockquote className="text-xl md:text-2xl font-bold text-white italic max-w-3xl mx-auto">{p.jordanQuote}</blockquote>
        </div>

        {/* CTA */}
        <div className="px-8 md:px-16 lg:px-24 py-16 text-center">
          <button onClick={()=>{ navigate('/contact'); window.scrollTo({top:0}); }} className="inline-flex items-center gap-3 border-2 text-white px-10 py-4 text-sm font-bold tracking-[0.2em] uppercase transition-all duration-500 group" style={{borderColor:'#A855F7'}} onMouseEnter={e=>e.currentTarget.style.backgroundColor='#A855F7'} onMouseLeave={e=>e.currentTarget.style.backgroundColor='transparent'}>
            {get(t.common.contactUs)} <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1"/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
